import { useState, useCallback } from 'react';
import type { ContractorInput, ShareholderInput, ChildInput } from '@/types';

// Default shareholder (director) values
const DEFAULT_DIRECTOR: ShareholderInput = {
  gross_salary: 12570,
  tax_code: '1257L',
  shareholding_percentage: 100,
  net_pension_contributions_percentage: 0,
  net_pension_contributions_amount: 0,
  net_pension_contributions_type: 'amount',
  pension_type: 'relief_at_source',
  payroll_giving_donations: 0,
  gift_aid_donations: 0,
  charity_type: 'gift_aid',
  self_employment_income: 0,
  other_income: 0,
};

// Default partner values (exported for use by query param initialization)
export const DEFAULT_PARTNER: ShareholderInput = {
  gross_salary: 12570,
  tax_code: '1257L',
  shareholding_percentage: 0,
  net_pension_contributions_percentage: 0,
  net_pension_contributions_amount: 0,
  net_pension_contributions_type: 'amount',
  pension_type: 'relief_at_source',
  payroll_giving_donations: 0,
  gift_aid_donations: 0,
  charity_type: 'gift_aid',
  self_employment_income: 0,
  other_income: 0,
};

// Default company values
const DEFAULT_COMPANY = {
  annual_revenue: 85000,
  business_expenses: 3000,
  retained_profits: 0,
  total_pension_contribution: 0,
  eligible_for_employment_allowance: false,
  associated_companies: 0,
};

// Default VAT values
const DEFAULT_VAT = {
  vat_registered: false,
  vat_using_flat_rate_scheme: false,
  vat_flat_rate_percentage: null as number | null,
  quarterly_goods_cost: null as number | null,
};

interface ContractorState {
  // Company
  annualRevenue: number;
  setAnnualRevenue: (value: number) => void;
  businessExpenses: number;
  setBusinessExpenses: (value: number) => void;
  retainedProfits: number;
  setRetainedProfits: (value: number) => void;
  totalPensionContribution: number;
  setTotalPensionContribution: (value: number) => void;
  eligibleForEmploymentAllowance: boolean;
  setEligibleForEmploymentAllowance: (value: boolean) => void;
  associatedCompanies: number;
  setAssociatedCompanies: (value: number) => void;

  // VAT
  vatRegistered: boolean;
  setVatRegistered: (value: boolean) => void;
  vatUsingFlatRateScheme: boolean;
  setVatUsingFlatRateScheme: (value: boolean) => void;
  vatFlatRatePercentage: number | null;
  setVatFlatRatePercentage: (value: number | null) => void;
  quarterlyGoodsCost: number | null;
  setQuarterlyGoodsCost: (value: number | null) => void;

  // Director & Partner (structured objects)
  director: ShareholderInput;
  setDirector: (value: ShareholderInput) => void;
  includePartner: boolean;
  setIncludePartner: (value: boolean) => void;
  partner: ShareholderInput | null;
  setPartner: (value: ShareholderInput | null) => void;

  // Children
  children: ChildInput[];
  setChildren: (value: ChildInput[]) => void;
  claimsChildBenefit: boolean;
  setClaimsChildBenefit: (value: boolean) => void;

  // Dividends
  totalDividends: number | null;
  setTotalDividends: (value: number | null) => void;
  retainThisYear: number | null;
  setRetainThisYear: (value: number | null) => void;

  // Getter
  getContractorInput: () => ContractorInput;
}

export function useContractorState(): ContractorState {
  // Company
  const [annualRevenue, setAnnualRevenue] = useState(DEFAULT_COMPANY.annual_revenue);
  const [businessExpenses, setBusinessExpenses] = useState(DEFAULT_COMPANY.business_expenses);
  const [retainedProfits, setRetainedProfits] = useState(DEFAULT_COMPANY.retained_profits);
  const [totalPensionContribution, setTotalPensionContribution] = useState(
    DEFAULT_COMPANY.total_pension_contribution
  );
  const [eligibleForEmploymentAllowance, setEligibleForEmploymentAllowance] = useState(
    DEFAULT_COMPANY.eligible_for_employment_allowance
  );
  const [associatedCompanies, setAssociatedCompanies] = useState(
    DEFAULT_COMPANY.associated_companies
  );

  // VAT
  const [vatRegistered, setVatRegistered] = useState(DEFAULT_VAT.vat_registered);
  const [vatUsingFlatRateScheme, setVatUsingFlatRateScheme] = useState(
    DEFAULT_VAT.vat_using_flat_rate_scheme
  );
  const [vatFlatRatePercentage, setVatFlatRatePercentage] = useState(
    DEFAULT_VAT.vat_flat_rate_percentage
  );
  const [quarterlyGoodsCost, setQuarterlyGoodsCost] = useState(DEFAULT_VAT.quarterly_goods_cost);

  // Director & Partner (structured)
  const [director, setDirector] = useState<ShareholderInput>(DEFAULT_DIRECTOR);
  const [includePartner, setIncludePartner] = useState(false);
  const [partner, setPartner] = useState<ShareholderInput | null>(null);

  // When partner is toggled on, initialize with defaults
  const handleSetIncludePartner = (value: boolean): void => {
    setIncludePartner(value);
    if (value && !partner) {
      setPartner(DEFAULT_PARTNER);
    }
  };

  // Children
  const [children, setChildren] = useState<ChildInput[]>([]);
  const [claimsChildBenefit, setClaimsChildBenefit] = useState(false);

  // Dividends
  const [totalDividends, setTotalDividends] = useState<number | null>(null);
  const [retainThisYear, setRetainThisYear] = useState<number | null>(null);

  // Build contractor input payload
  const getContractorInput = useCallback((): ContractorInput => {
    // Helper to get personal pension amount based on pension type
    const getPersonalPension = (shareholder: ShareholderInput): number => {
      if (shareholder.pension_type !== 'relief_at_source') {
        return 0; // Only relief_at_source counts as personal pension
      }
      return shareholder.net_pension_contributions_type === 'percentage'
        ? shareholder.net_pension_contributions_percentage ?? 0
        : shareholder.net_pension_contributions_amount ?? 0;
    };

    // Helper to get charitable donations based on charity type
    const getCharitableDonations = (shareholder: ShareholderInput): number => {
      return shareholder.charity_type === 'gift_aid'
        ? shareholder.gift_aid_donations ?? 0
        : shareholder.payroll_giving_donations ?? 0;
    };

    const input: ContractorInput = {
      // Company
      annual_revenue: annualRevenue,
      business_expenses: businessExpenses,
      retained_profits: retainedProfits,
      total_pension_contribution: totalPensionContribution,
      eligible_for_employment_allowance: eligibleForEmploymentAllowance,
      associated_companies: associatedCompanies,

      // VAT
      vat_registered: vatRegistered,
      vat_using_flat_rate_scheme: vatUsingFlatRateScheme,
      vat_flat_rate_percentage: vatFlatRatePercentage,
      quarterly_goods_cost: quarterlyGoodsCost,

      // Director
      director_salary: director.gross_salary,
      director_shareholding_percentage: director.shareholding_percentage,
      director_self_employment_income: director.self_employment_income ?? 0,
      director_other_income: director.other_income ?? 0,
      director_tax_code: director.tax_code,
      director_personal_pension: getPersonalPension(director),
      director_charitable_donations: getCharitableDonations(director),

      // Children
      children,
      claims_child_benefit: claimsChildBenefit,

      // Dividends
      total_dividends: totalDividends,
      retain_this_year: retainThisYear,
    };

    // Include partner if enabled
    if (includePartner && partner) {
      input.partner_salary = partner.gross_salary;
      input.partner_shareholding_percentage = partner.shareholding_percentage;
      input.partner_self_employment_income = partner.self_employment_income ?? 0;
      input.partner_other_income = partner.other_income ?? 0;
      input.partner_tax_code = partner.tax_code;
      input.partner_personal_pension = getPersonalPension(partner);
      input.partner_charitable_donations = getCharitableDonations(partner);
    }

    return input;
  }, [
    annualRevenue,
    businessExpenses,
    retainedProfits,
    totalPensionContribution,
    eligibleForEmploymentAllowance,
    associatedCompanies,
    vatRegistered,
    vatUsingFlatRateScheme,
    vatFlatRatePercentage,
    quarterlyGoodsCost,
    director,
    includePartner,
    partner,
    children,
    claimsChildBenefit,
    totalDividends,
    retainThisYear,
  ]);

  return {
    // Company
    annualRevenue,
    setAnnualRevenue,
    businessExpenses,
    setBusinessExpenses,
    retainedProfits,
    setRetainedProfits,
    totalPensionContribution,
    setTotalPensionContribution,
    eligibleForEmploymentAllowance,
    setEligibleForEmploymentAllowance,
    associatedCompanies,
    setAssociatedCompanies,

    // VAT
    vatRegistered,
    setVatRegistered,
    vatUsingFlatRateScheme,
    setVatUsingFlatRateScheme,
    vatFlatRatePercentage,
    setVatFlatRatePercentage,
    quarterlyGoodsCost,
    setQuarterlyGoodsCost,

    // Director & Partner
    director,
    setDirector,
    includePartner,
    setIncludePartner: handleSetIncludePartner,
    partner,
    setPartner,

    // Children
    children,
    setChildren,
    claimsChildBenefit,
    setClaimsChildBenefit,

    // Dividends
    totalDividends,
    setTotalDividends,
    retainThisYear,
    setRetainThisYear,

    // Getter
    getContractorInput,
  };
}
